/**
 * Section 2 — attachments.
 *
 * The order badge on each thumbnail is load-bearing, not decoration: a carousel
 * publishes in attachment order, so the user has to be able to see and change
 * it. Reordering is drag-and-drop with arrow-key fallback, because a
 * drag-only control is unusable by keyboard and the order is not optional.
 */

import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { AlertCircle, Play, Plus, Trash2, UploadCloud } from 'lucide-react';

import { cn } from '../../../lib/utils';
import { useTranslation } from '../../../context/LanguageContext';
import { PLATFORM_CONFIGS } from '../../../config/platforms';
import type { PlatformId } from '../../../config/platforms';
import type { ComposerMedia } from '../../../types/composer';

/** Lets the parent open the file picker from the editor's toolbar. */
export interface MediaUploadHandle {
  openPicker: () => void;
}

interface MediaUploadProps {
  media: ComposerMedia[];
  selectedPlatforms: PlatformId[];
  onAddFiles: (files: File[]) => void;
  onRemove: (id: string) => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
}

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime';

export const MediaUpload = forwardRef<MediaUploadHandle, MediaUploadProps>(function MediaUpload(
  { media, selectedPlatforms, onAddFiles, onRemove, onReorder },
  ref
) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  useImperativeHandle(ref, () => ({ openPicker: () => inputRef.current?.click() }), []);

  const handleFiles = (files: FileList | null) => {
    if (files && files.length > 0) onAddFiles(Array.from(files));
  };

  // The binding limit is whichever selected platform allows the fewest, since
  // one attachment set has to satisfy all of them at once.
  const imageCount = media.filter((m) => m.type === 'image').length;
  const maxImages = selectedPlatforms.length
    ? Math.min(...selectedPlatforms.map((p) => PLATFORM_CONFIGS[p].mediaRule.maxImages))
    : Infinity;
  const overImageLimit = imageCount > maxImages;

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = '';
        }}
      />

      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (draggingIndex === null) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          'flex flex-wrap gap-3 rounded-lg transition-colors',
          dragOver && 'bg-blue-50/60 ring-2 ring-blue-300'
        )}
      >
        {/* Upload tile */}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex h-[92px] w-[92px] shrink-0 flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed border-slate-300 bg-slate-50/50 text-slate-500 transition-colors hover:border-blue-400 hover:bg-blue-50/50 hover:text-blue-600"
        >
          <UploadCloud className="h-5 w-5" />
          <span className="text-[10px] font-semibold leading-tight">
            {t('composer.media.upload')}
          </span>
        </button>

        {media.map((item, index) => (
          <Thumbnail
            key={item.id}
            item={item}
            index={index}
            total={media.length}
            isDragging={draggingIndex === index}
            onDragStart={() => setDraggingIndex(index)}
            onDragEnd={() => setDraggingIndex(null)}
            onDropOn={() => {
              if (draggingIndex !== null && draggingIndex !== index) {
                onReorder(draggingIndex, index);
              }
              setDraggingIndex(null);
            }}
            onMove={(direction) => {
              const target = index + direction;
              if (target >= 0 && target < media.length) onReorder(index, target);
            }}
            onRemove={() => onRemove(item.id)}
          />
        ))}

        {/* Add-more tile */}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex h-[92px] w-[92px] shrink-0 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-slate-300 text-slate-400 transition-colors hover:border-blue-400 hover:text-blue-600"
        >
          <Plus className="h-4 w-4" />
          <span className="text-[10px] font-semibold">{t('composer.media.addMore')}</span>
        </button>
      </div>

      <p className="mt-2.5 text-[11px] text-slate-500">{t('composer.media.dropHint')}</p>
      <p className="text-[11px] text-slate-400">{t('composer.media.types')}</p>

      {overImageLimit && (
        <p className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-rose-600">
          <AlertCircle className="h-3.5 w-3.5" />
          {t('composer.media.tooManyImages', { max: maxImages })}
        </p>
      )}
    </div>
  );
});

interface ThumbnailProps {
  item: ComposerMedia;
  index: number;
  total: number;
  isDragging: boolean;
  onDragStart: () => void;
  onDragEnd: () => void;
  onDropOn: () => void;
  onMove: (direction: -1 | 1) => void;
  onRemove: () => void;
}

function Thumbnail({
  item,
  index,
  total,
  isDragging,
  onDragStart,
  onDragEnd,
  onDropOn,
  onMove,
  onRemove,
}: Readonly<ThumbnailProps>) {
  const { t } = useTranslation();
  const uploading = !item.uploaded && item.uploadError === null;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        onDropOn();
      }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          onMove(-1);
        }
        if (e.key === 'ArrowRight') {
          e.preventDefault();
          onMove(1);
        }
      }}
      tabIndex={0}
      role="listitem"
      aria-label={t('composer.media.itemLabel', { name: item.name, position: index + 1, total })}
      className={cn(
        'group relative h-[92px] w-[92px] shrink-0 cursor-grab overflow-hidden rounded-lg border border-slate-200 bg-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1',
        isDragging && 'opacity-40'
      )}
    >
      <img src={item.previewUrl} alt={item.name} className="h-full w-full object-cover" />

      {/* Publish-order badge */}
      <span className="absolute right-1.5 top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-md bg-black/65 px-1 text-[10px] font-bold text-white">
        {index + 1}
      </span>

      {item.type === 'video' && (
        <>
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-black/55">
              <Play className="h-3 w-3 fill-white text-white" />
            </span>
          </span>
          {item.durationMs !== null && (
            <span className="absolute bottom-1.5 left-1.5 rounded bg-black/65 px-1 py-0.5 text-[9px] font-semibold text-white tabular-nums">
              {formatDuration(item.durationMs)}
            </span>
          )}
        </>
      )}

      {uploading && (
        <span className="absolute inset-x-0 bottom-0 h-1 bg-black/40">
          <span
            className="block h-full bg-blue-500 transition-[width] duration-200"
            style={{ width: `${item.uploadPercent}%` }}
          />
        </span>
      )}

      {item.uploadError && (
        <span className="absolute inset-0 flex items-center justify-center bg-rose-600/80">
          <AlertCircle className="h-4 w-4 text-white" />
        </span>
      )}

      <button
        type="button"
        onClick={onRemove}
        aria-label={t('composer.media.remove', { name: item.name })}
        className="absolute left-1.5 top-1.5 flex h-[18px] w-[18px] items-center justify-center rounded-md bg-black/65 text-white opacity-0 transition-opacity hover:bg-rose-600 focus:opacity-100 group-hover:opacity-100"
      >
        <Trash2 className="h-2.5 w-2.5" />
      </button>
    </div>
  );
}

function formatDuration(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}
